import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import { useTranslations } from 'next-intl';
import { useMemo } from 'react';

import { FormattedMessage } from '@flipster/i18n';
import { Tooltip } from '@flipster/react/design-system-legacy/tooltip';
import { Badge } from '@flipster/react/design-system/badge';
import { Dialog, dialog } from '@flipster/react/design-system/dialog';
import { TextButton } from '@flipster/react/design-system/text-button';
import Formatted from '@flipster/react/design-utils/formatted';
import InformationCircleIcon from '@flipster/react/icon/generated/InformationCircleIcon';
import { css, cx } from '@flipster/styled-system/css';
import { hstack, stack } from '@flipster/styled-system/patterns';
import { token } from '@flipster/styled-system/tokens';
import { FeePrecision, UsdtCurrency, getQuoteCurrencyPrecision } from '@flipster/utils/constants/currency';
import Num from '@flipster/utils/num';
import segment from '@flipster/utils/segment';
import { DisplayPnlEvent } from '@flipster/utils/segment/events/display-pnl';

import { MovableModal } from '../movable-modal';
import { pnlModeBunja } from '../pnl-setting-modal/state';
import { getDisplayPnl } from '../pnl/get-display-pnl';
import { useSnapshotSpec } from '../snapshot/use-snapshot-spec';
// ROLLBACK(last-price): uPnL basis 셀렉터 재활성화 시 복원.
// import { UpnlBasisSelect } from '../upnl-basis-select';
import { upnlBasisBunja } from '../upnl-basis/state';
import { pnlModalBunja } from './state';

interface Props {
  symbol: string;
  onClickNext?: () => void;
  onClose: () => void;
}

const PnlContent = ({ onClose, symbol, onClickNext }: Props) => {
  const t = useTranslations();
  const { instrumentsKeyBySymbol } = useSnapshotSpec();
  const { pnlModeAtom } = useBunja(pnlModeBunja);
  const pnlMode = useAtomValue(pnlModeAtom);
  const { upnlBasisAtom } = useBunja(upnlBasisBunja);
  // ROLLBACK(last-price): 셀렉터 재활성화 시 setter 복원 — const [upnlBasis, setUpnlBasis] = useAtom(upnlBasisAtom);
  const upnlBasis = useAtomValue(upnlBasisAtom);

  const { positionAtom } = useBunja(pnlModalBunja);
  const position = useAtomValue(positionAtom);
  const quoteCurrency = instrumentsKeyBySymbol?.[symbol]?.quoteCurrency ?? UsdtCurrency;
  const quotePrecision = getQuoteCurrencyPrecision(quoteCurrency);

  const displayUnrealizedPnl = useMemo(
    () => (position ? getDisplayPnl(position, { pnlMode: 'unrealizedPnl', upnlBasis }) : undefined),
    [position, upnlBasis],
  );
  const displayNetPnl = useMemo(
    () => (position ? getDisplayPnl(position, { pnlMode: 'netPnl', upnlBasis }) : undefined),
    [position, upnlBasis],
  );

  const tradingFee = Num(position?.cumTradingFee ?? '0')
    .multipliedBy(-1)
    .toString();

  const fundingFee = Num(position?.cumFundingFee ?? '0')
    .multipliedBy(-1)
    .toString();

  return (
    <>
      <MovableModal.Content>
        <dl
          className={hstack({
            alignItems: 'stretch',
            gap: 6,
            mb: 10,
            '& > div': {
              display: 'flex',
              flex: 1,
              gap: 2,
              justifyContent: 'space-between',
              flexDirection: 'column',
              rounded: 8,
              bgColor: 'background.default.level-1',
              py: 12,
              px: 10,
            },
            '& > div > dt': { textStyle: 'Text/M/Regular', color: 'content.default.level-5' },
          })}
        >
          <div>
            <dt
              className={hstack({
                gap: 4,
                justify: 'space-between',
                alignItems: 'center',
                textStyle: 'Text/S/Regular',
              })}
            >
              <p>
                <FormattedMessage id='Autogen_common.r251126.s_Unrealized_PnL' />
              </p>
              {pnlMode === 'unrealizedPnl' && <CurrentBadge />}
            </dt>
            <dd>
              <Formatted.Number
                value={displayUnrealizedPnl}
                precision={quotePrecision}
                suffix={quoteCurrency}
                css={{ textStyle: 'Text/L/Monospace/Strong' }}
                withColor
                withPlus
                toFixed
              />
            </dd>
          </div>
          <div>
            <dt
              className={hstack({
                textStyle: 'Text/S/Regular',
                alignItems: 'flex-start',
                justify: 'space-between',
                gap: 4,
              })}
            >
              <div className={hstack({ gap: 4, alignItems: 'flex-start' })}>
                <FormattedMessage id='Assets.r250821.Net_p_l' />
                <Tooltip
                  placement='bottom'
                  maxWidth={275}
                  mobileAlert={() => {
                    dialog.open({
                      title: {
                        label: <FormattedMessage id='Assets.r250821.Net_p_l' />,
                      },
                      content: (
                        <FormattedMessage id='Autogen_common.r251126.s_Profit_or_loss_after_fees_and_realized_gains_losses' />
                      ),
                      buttons: {
                        primary: <Dialog.Button hierarchy='primary' label='Confirm' />,
                      },
                    });
                  }}
                >
                  <Tooltip.Trigger asChild>
                    <InformationCircleIcon
                      size={16}
                      color={token('colors.content.default.level-1')}
                      className={css({
                        flexShrink: 0,
                        mt: 2,
                      })}
                    />
                  </Tooltip.Trigger>
                  <Tooltip.Content css={{ textStyle: 'Text/S/Regular', px: 12 }}>
                    <FormattedMessage id='Autogen_common.r251126.s_Profit_or_loss_after_fees_and_realized_gains_losses' />
                  </Tooltip.Content>
                </Tooltip>
              </div>
              {pnlMode === 'netPnl' && <CurrentBadge />}
            </dt>
            <dd>
              <Formatted.Number
                value={displayNetPnl}
                precision={quotePrecision}
                suffix={quoteCurrency}
                css={{ textStyle: 'Text/L/Monospace/Strong' }}
                withColor
                withPlus
                toFixed
              />
            </dd>
          </div>
        </dl>
        <div className={hstack({ gap: 16, justify: 'space-between', mb: 32 })}>
          <p className={cx(defaultDtCss, css({ whiteSpace: 'pre-line' }))}>
            <FormattedMessage id='Autogen_common.r251126.s_Switch_P_L_display_setting' />
            <br />
            <FormattedMessage id='Autogen_common.r251126.s_for_all_open_positions' />
          </p>
          <TextButton
            label={t('Autogen_common.r251126.s_Go_to_settings')}
            size='medium'
            color='neutral'
            onClick={() => {
              onClickNext?.();
              segment.track(DisplayPnlEvent.DisplayPnlSettingsClick);
            }}
          />
        </div>
        <p className={css({ textStyle: 'Title/XS', color: 'content.default.level-5', mb: 12 })}>
          <FormattedMessage id='Autogen_common.r251126.s_Breakdown' />
        </p>
        <dl
          className={stack({
            py: 8,
            px: 20,
            gap: 8,
            borderWidth: '1px',
            borderStyle: 'solid',
            borderColor: 'border.default.level-1',
            rounded: 8,
            '& > div': {
              display: 'flex',
            },
            '& > div > dt': { flex: 1 },
          })}
        >
          <div
            className={stack({
              py: 12,
              gap: 4,
              borderBottomWidth: '1px',
              borderBottomStyle: 'solid',
              borderColor: 'border.default.level-1',
            })}
          >
            <div className={css({ display: 'flex' })}>
              <dt className={cx(highlightDtCss, css({ flex: 1 }))}>
                <FormattedMessage id='Common.r251107.Unrealized_pnl' />
                <p className={numberBadgeCss}>1</p>
              </dt>
              <dd>
                <Formatted.Number
                  value={displayUnrealizedPnl}
                  precision={quotePrecision}
                  suffix={quoteCurrency}
                  css={highlightNumCss}
                  withColor
                  withPlus
                  toFixed
                />
              </dd>
            </div>
            {/* ROLLBACK(last-price): uPnL basis(Mark/Last/BBO) 셀렉터 — MM 준비 완료 시 재활성화.
            <div className={hstack({ justify: 'flex-end' })}>
              <UpnlBasisSelect value={upnlBasis} onChange={setUpnlBasis} />
            </div> */}
          </div>
          <div className={css({ py: 4 })}>
            <dt className={highlightDtCss}>
              <FormattedMessage id='Common.r251107.Realized_pnl' />
              <p className={numberBadgeCss}>2</p>
            </dt>
            <dd>
              <Formatted.Number
                value={position?.netRealizedPnl}
                precision={quotePrecision}
                suffix={quoteCurrency}
                css={highlightNumCss}
                withColor
                withPlus
                toFixed
              />
            </dd>
          </div>
          <div>
            <dt className={defaultDtCss}>
              <FormattedMessage id='Autogen_common.r251126.s_Closed_PnL' />
            </dt>
            <dd>
              <Formatted.Number
                value={position?.grossRealizedPnl}
                precision={quotePrecision}
                suffix={quoteCurrency}
                css={defaultNumCss}
                withColor
                withPlus
                toFixed
              />
            </dd>
          </div>
          <div>
            <dt className={defaultDtCss}>
              <FormattedMessage id='Autogen_common.r251126.s_Funding_fee' />
            </dt>
            <dd>
              <Formatted.Number
                value={fundingFee}
                precision={FeePrecision}
                suffix={quoteCurrency}
                css={defaultNumCss}
                withColor
                withPlus
                toFixed
              />
            </dd>
          </div>
          <div>
            <dt className={defaultDtCss}>
              <FormattedMessage id='Autogen_common.r251126.s_Trading_fee' />
            </dt>
            <dd>
              <Formatted.Number
                value={tradingFee}
                precision={FeePrecision}
                suffix={quoteCurrency}
                css={defaultNumCss}
                withColor
                withPlus
                toFixed
              />
            </dd>
          </div>
          <div className={css({ textStyle: 'Text/M/Regular', color: 'content.default.level-1' })}>
            <FormattedMessage id='Pnl.r260128.Feediscount_description' />
          </div>
          <div
            className={css({
              mt: 12,
              py: 12,
              borderTopWidth: '1px',
              borderTopStyle: 'solid',
              borderColor: 'border.default.level-1',
            })}
          >
            <dt className={highlightDtCss}>
              <FormattedMessage id='Assets.r250821.Net_p_l' />
              <div className={hstack({ gap: 2 })}>
                <p className={numberBadgeCss}>1</p>+<p className={numberBadgeCss}>2</p>
              </div>
            </dt>
            <dd>
              <Formatted.Number
                value={displayNetPnl}
                precision={quotePrecision}
                suffix={quoteCurrency}
                css={highlightNumCss}
                withColor
                withPlus
                toFixed
              />
            </dd>
          </div>
        </dl>
      </MovableModal.Content>
      <MovableModal.Footer>
        <MovableModal.Button
          hierarchy='primary'
          size='large'
          color='accent'
          label={t('Autogen_common.r251126.s_Done')}
          onClick={onClose}
        />
      </MovableModal.Footer>
    </>
  );
};

export default PnlContent;

const numberBadgeCss = css({
  w: 20,
  h: 20,
  p: 2,
  color: 'content.default.level-1',
  rounded: 999,
  bgColor: 'background.default.level-3',
  textStyle: 'Text/XS/Strong',
  textAlign: 'center',
});

const defaultDtCss = css({ textStyle: 'Text/M/Regular', color: 'content.default.level-2' });
const highlightDtCss = hstack({ gap: 8, textStyle: 'Text/L/Regular', color: 'content.default.level-5' });
const defaultNumCss = { textStyle: 'Text/M/Monospace/Regular', color: 'content.default.level-5' };
const highlightNumCss = { textStyle: 'Text/L/Monospace/Strong', color: 'content.default.level-5' };

const CurrentBadge = () => {
  return (
    <div className={css({ alignSelf: 'stretch' })}>
      <Badge type='tint' color='accent'>
        <FormattedMessage id='Autogen_common.r251126.s_Current' />
      </Badge>
    </div>
  );
};
